import { goto } from '$app/navigation'
import type { ModalSettings, ModalStore, ToastStore } from '@skeletonlabs/skeleton'
import type { QueryFunction } from '@tanstack/svelte-query'
import camelcaseKeys from 'camelcase-keys'

import type { IRawUnlearningRun, IUnlearningRun, RunInfo } from './unlearning-run.svelte'
import type { ICreateModel, IModel } from '../models/models-store.svelte'
import { encodeHTMLEntities } from '@utils/encode'
import { FetchError, handleFetch } from '@utils/fetch'
import { modalRegistry } from '@utils/modal-registry.svelte'
import { prepareRestorableModal } from '@utils/modal-restore'
import { queryClient } from '@utils/svelte-query-client'

const parseUnlearningRun = (unlearningRun: IRawUnlearningRun): IUnlearningRun => ({
  ...unlearningRun,
  createdAt: new Date(unlearningRun.createdAt),
  deletedAt: unlearningRun.deletedAt ? new Date(unlearningRun.deletedAt) : null,
})

const unlearningRunsQuery: ({
  archived,
}: {
  archived?: boolean
}) => QueryFunction<Map<number, IUnlearningRun>> =
  ({ archived = false }: { archived?: boolean }) =>
  async ({ signal }) => {
    try {
      const unlearningRuns: IRawUnlearningRun[] = camelcaseKeys(
        await (
          await handleFetch(`/unlearning/run/list?archived=${archived}`, {
            signal,
          })
        ).json(),
        { deep: true },
      )
      const unlearningRunMap = unlearningRuns.reduce((acc, unlearningRun) => {
        acc.set(unlearningRun.id, parseUnlearningRun(unlearningRun))
        return acc
      }, new Map<number, IUnlearningRun>())
      return unlearningRunMap
    } catch (err) {
      console.error('Failed to load unlearning runs: ', err)
      throw err
    }
  }

const unlearningRunsQueryKey = (archived?: boolean) => ['unlearning-runs', archived]

/**
 * Loads the unlearning runs from the server.
 * Invalidates the query cache to ensure the data is fresh.
 */
const loadUnlearningRuns = async ({ archived = false }: { archived?: boolean } = {}) => {
  await queryClient.invalidateQueries({ queryKey: unlearningRunsQueryKey(archived) })
  await queryClient.fetchQuery({
    queryKey: unlearningRunsQueryKey(archived),
    queryFn: unlearningRunsQuery({ archived }),
  })
}

const runUnlearning = async (
  model: $state.Snapshot<ICreateModel | IModel>,
  modelId: number,
  toastStore: ToastStore,
  modalStore: ModalStore,
  modelName: string | undefined,
) => {
  const runUnlearningHandler = async (runInfo: RunInfo) => {
    if (!runInfo) {
      return
    }
    try {
      await Promise.all([
        handleFetch(`/unlearning/run/${modelId}`, {
          method: 'POST',
          body: runInfo,
        }),
        new Promise((resolve) => setTimeout(resolve, 1_000)),
      ])
      toastStore.trigger({
        message: `Started unlearning run for model named <strong>${encodeHTMLEntities(modelName ?? '')}</strong>`,
        background: 'variant-filled-success',
      })
      await goto('/unlearn-data/runs')
    } catch (err) {
      console.error(err)
      if (err instanceof FetchError && err.message) {
        toastStore.trigger({
          message: encodeHTMLEntities(`Failed to launch unlearning run with error: ${err.message}`),
          background: 'variant-filled-error',
          autohide: false,
        })
        throw err
      }
      toastStore.trigger({
        message: 'Failed to launch unlearning run',
        background: 'variant-filled-error',
      })
      throw err
    }
  }

  const modal: ModalSettings = {
    type: 'component',
    component: modalRegistry.runUnlearningModal,
    // Data
    title: 'Run unlearning',
    body: 'Please choose your dataset to run the unlearning. You may also choose to set advanced options',
    buttonTextConfirm: 'Run now',
    value: model,

    response: (runInfo: RunInfo) => void runUnlearningHandler(runInfo),
  }
  modalStore.trigger(
    prepareRestorableModal(modalStore, toastStore, modal, {
      toastMessage: 'Run unlearning closed. Reopen to restore your changes?',
    }),
  )
}

const archiveRun = async (runId: string) => {
  await (
    await handleFetch(`/unlearning/run/archive/${runId}`, {
      method: 'PATCH',
    })
  ).json()
}

const restoreRun = async (runId: string) => {
  await (
    await handleFetch(`/unlearning/run/restore/${runId}`, {
      method: 'PATCH',
    })
  ).json()
}

export type { IUnlearningRun }
export {
  archiveRun,
  loadUnlearningRuns,
  restoreRun,
  runUnlearning,
  unlearningRunsQuery,
  unlearningRunsQueryKey,
}
